import type { SubjectOf } from './subjects'
import type { LibraryDocument } from './library'
import type { Note } from './notes'
import { type Failure, RequestFailed, malformed, readJson, request } from './request'
import type { Task } from './tasks'

/**
 * The boards, as the service describes them.
 *
 * **A board arrives whole**: the board, everything lying on it, every line drawn on it, and the
 * content behind every card already resolved. One request, because a surface cannot be drawn in
 * instalments — half the cards in place and half of them arriving is a layout that jumps under
 * somebody's hands.
 *
 * A card carries the whole {@link LibraryDocument}, {@link Note} or {@link Task}, so it is drawn
 * exactly as the library, the pad and the list draw it — same picture, same first line, same
 * sentence — without a second mapping of everything any of them is. **A card whose content has gone
 * carries none of them**, and its `label` is what says what lay there.
 */

export type ElementKind = 'card' | 'sticky' | 'text' | 'frame'
export type DisplayMode = 'preview' | 'document' | 'presentation' | 'writing'
/**
 * Where on an element a line is fastened.
 *
 * `auto` takes the shortest way and meets the edge wherever the other end happens to be; everything
 * else is a place somebody chose and stays exactly there — the four sides, and the four corners.
 */
export type Anchor =
  | 'auto'
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'top-left'
  | 'top-right'
  | 'bottom-right'
  | 'bottom-left'
export type Routing = 'straight' | 'elbow' | 'curved'
export type Heads = 'none' | 'to' | 'both'

export interface Board {
  readonly id: string
  readonly name: string
  /** Whether this is the board signing in leads to. Exactly one board per space carries it. */
  readonly isDefault: boolean
  readonly createdAt: string
  readonly updatedAt: string
  /** The subjects this board belongs to — the board itself, never what lies on it. */
  readonly subjects?: readonly SubjectOf[]
}

export interface BoardElement {
  readonly id: string
  readonly kind: ElementKind
  /** `document`, `note` or `task` for a card; `null` for everything that exists only here. */
  readonly contentKind: string | null
  readonly contentId: string | null
  /** What was written on it when it was put down. What a card says once its content has gone. */
  readonly label: string | null
  readonly text: string | null
  readonly format: string | null
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly z: number
  readonly colour: string | null
  readonly highlighted: boolean
  /** Whether it survives the board being cleared — and nothing more than that. */
  readonly pinned: boolean
  /** Whether it refuses to be moved, resized or written in until it is unlocked. */
  readonly locked: boolean
  /** `fit` or `fill` — how the picture of a card fills its tile. `null` is the ordinary one, `fit`. */
  readonly fit: string | null
  /**
   * How a document card is drawn. `null` is the ordinary static preview.
   *
   * Which of them a card can actually take depends on the document behind it: `presentation` on a
   * PDF, `writing` on something MyLib can write in. A mode a card cannot take draws the preview.
   */
  readonly displayMode: Exclude<DisplayMode, 'preview'> | null
  /** Last page shown in presentation mode, counted from one. */
  readonly displayPage: number
  readonly createdAt: string
  readonly updatedAt: string
  readonly document: LibraryDocument | null
  readonly note: Note | null
  readonly task: Task | null
}

/** One end of a line: an element and the side it leaves from, or a bare point on the surface. */
export interface End {
  readonly element?: string | null
  readonly anchor?: Anchor | null
  readonly x?: number | null
  readonly y?: number | null
}

export interface Point {
  readonly x: number
  readonly y: number
}

export interface BoardConnection {
  readonly id: string
  readonly from: End
  readonly to: End
  readonly routing: Routing
  readonly waypoints: readonly Point[]
  readonly heads: Heads
  readonly colour: string | null
  /** Whether it survives the board being cleared — the same protection an element has. */
  readonly pinned: boolean
  readonly createdAt: string
  readonly updatedAt: string
}

export interface OpenBoard {
  readonly board: Board
  readonly elements: readonly BoardElement[]
  readonly connections: readonly BoardConnection[]
}

/** Where a card on some piece of content lies: which board, and which element on it. */
export interface Holding {
  readonly board: Board
  readonly element: string
}

/** Where something lies and how big it is, in board coordinates. */
export interface Place {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly z?: number
}

const BOARDS = '/api/boards'

export async function fetchBoards(signal?: AbortSignal): Promise<Board[]> {
  const response = await request(BOARDS, { signal })
  const payload: unknown = await response.json().catch(() => null)
  if (!Array.isArray(payload)) {
    throw malformed('The answer did not describe any boards.')
  }
  return payload as Board[]
}

export async function fetchBoard(id: string, signal?: AbortSignal): Promise<OpenBoard> {
  const payload = await readJson<Partial<OpenBoard>>(
    await request(`${BOARDS}/${encodeURIComponent(id)}`, { signal, expect: [404] }),
  )
  if (payload.board === undefined || !Array.isArray(payload.elements)) {
    throw malformed('The answer did not describe a board.')
  }
  return {
    board: payload.board,
    elements: payload.elements,
    connections: payload.connections ?? [],
  }
}

export async function createBoard(name: string): Promise<Board> {
  return (await change(BOARDS, 'POST', { name })) as Board
}

export async function renameBoard(id: string, name: string): Promise<Board> {
  return (await change(`${BOARDS}/${encodeURIComponent(id)}/name`, 'PUT', { name })) as Board
}

/** Makes this the board signing in leads to. Whichever board had that mark loses it. */
export async function makeBoardDefault(id: string): Promise<Board> {
  return (await change(`${BOARDS}/${encodeURIComponent(id)}/default`, 'PUT')) as Board
}

/**
 * Copies a board with everything on it.
 *
 * This is what a snapshot is: an ordinary board from the moment it exists. Sticky notes are copied
 * and go their own way; cards stay references, so the copy shows **today's** library at the old
 * places.
 */
export async function duplicateBoard(id: string, name: string): Promise<Board> {
  return (await change(`${BOARDS}/${encodeURIComponent(id)}/duplicate`, 'POST', { name })) as Board
}

/** Clears a board. Everything that is not pinned goes, for good — the answer says how much did. */
export async function clearBoard(id: string): Promise<number> {
  const answer = (await change(`${BOARDS}/${encodeURIComponent(id)}/clear`, 'POST')) as {
    removed?: number
  }
  return answer.removed ?? 0
}

export async function deleteBoard(id: string): Promise<void> {
  await change(`${BOARDS}/${encodeURIComponent(id)}`, 'DELETE')
}

export interface PutOnBoard {
  readonly kind: ElementKind
  readonly contentKind?: string
  readonly contentId?: string
  readonly label?: string
  readonly text?: string
  readonly format?: string
  readonly colour?: string
  readonly place: Place
}

export async function putOnBoard(board: string, what: PutOnBoard): Promise<BoardElement> {
  return (await change(
    `${BOARDS}/${encodeURIComponent(board)}/elements`,
    'POST',
    what,
  )) as BoardElement
}

/**
 * Moves and resizes a whole selection in **one** request.
 *
 * The call the surface stands or falls on: dragging twenty cards is one gesture, so it is one
 * request. Anything the service no longer knows about is left out of the answer rather than
 * refusing the lot.
 */
export async function placeOnBoard(
  board: string,
  places: readonly { readonly id: string; readonly place: Place }[],
): Promise<BoardElement[]> {
  return (await change(
    `${BOARDS}/${encodeURIComponent(board)}/elements/places`,
    'PUT',
    places,
  )) as BoardElement[]
}

/** Brings elements in front of everything on the board, or puts them behind it. */
export async function changeDepth(
  board: string,
  ids: readonly string[],
  toFront: boolean,
): Promise<BoardElement[]> {
  return (await change(`${BOARDS}/${encodeURIComponent(board)}/elements/depth`, 'PUT', {
    ids,
    toFront,
  })) as BoardElement[]
}

export interface ElementChange {
  readonly text?: string
  /** The markup the text is written in, as a media type. Absent means "leave it alone". */
  readonly format?: string
  readonly label?: string
  /** A colour, or {@link NO_COLOUR} to take one off again. Absent means "leave it alone". */
  readonly colour?: string
  readonly highlighted?: boolean
  readonly pinned?: boolean
  readonly locked?: boolean
  /** `fit`, `fill`, or {@link NO_COLOUR} to go back to the ordinary one. */
  readonly fit?: string
  /** `preview`, `document` or `presentation`. */
  readonly displayMode?: DisplayMode
  /** The presentation page to remember, counted from one. */
  readonly displayPage?: number
}

/** What is sent to take a colour off. An absent field already means "leave it as it is". */
export const NO_COLOUR = 'none'

export async function changeElement(
  board: string,
  element: string,
  wanted: ElementChange,
): Promise<BoardElement> {
  return (await change(
    `${BOARDS}/${encodeURIComponent(board)}/elements/${encodeURIComponent(element)}`,
    'PUT',
    wanted,
  )) as BoardElement
}

/**
 * Takes something off a board.
 *
 * For a card that is all it is — the document behind it is untouched and stays in the library. For a
 * sticky note it is a deletion, because there is nowhere else it exists.
 */
export async function takeOffBoard(board: string, element: string): Promise<void> {
  await change(
    `${BOARDS}/${encodeURIComponent(board)}/elements/${encodeURIComponent(element)}`,
    'DELETE',
  )
}

export interface DrawnLine {
  readonly from: End
  readonly to: End
  readonly routing: Routing
  readonly waypoints: readonly Point[]
  readonly heads: Heads
  readonly colour?: string | null
  readonly pinned?: boolean
}

export async function connect(board: string, line: DrawnLine): Promise<BoardConnection> {
  return (await change(
    `${BOARDS}/${encodeURIComponent(board)}/connections`,
    'POST',
    line,
  )) as BoardConnection
}

export async function reconnect(
  board: string,
  id: string,
  line: DrawnLine,
): Promise<BoardConnection> {
  return (await change(
    `${BOARDS}/${encodeURIComponent(board)}/connections/${encodeURIComponent(id)}`,
    'PUT',
    line,
  )) as BoardConnection
}

export async function disconnect(board: string, id: string): Promise<void> {
  await change(
    `${BOARDS}/${encodeURIComponent(board)}/connections/${encodeURIComponent(id)}`,
    'DELETE',
  )
}

/**
 * Which boards hold a card on this piece of content.
 *
 * What an open document shows as "lies on". Asked of the boards rather than of the library: a board
 * knows what it holds, and a document deliberately knows nothing about who is looking at it.
 */
export async function boardsHolding(
  kind: string,
  id: string,
  signal?: AbortSignal,
): Promise<Holding[]> {
  const response = await request(
    `${BOARDS}/holding/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`,
    { signal },
  )
  const payload: unknown = await response.json().catch(() => null)
  return Array.isArray(payload) ? (payload as Holding[]) : []
}

/** What a card on this element points at, as the search index names it. */
export function contentRefOf(element: BoardElement): { kind: string; id: string } | null {
  return element.contentKind === null || element.contentId === null
    ? null
    : { kind: element.contentKind, id: element.contentId }
}

/**
 * What an element is called on the board.
 *
 * The content's own title where the content is still there, so a renamed document is shown under its
 * new name; the copied caption where it is gone, which is what keeps an arrangement readable.
 */
export function titleOf(element: BoardElement): string {
  if (element.document !== null) {
    return element.document.title
  }
  if (element.task !== null) {
    return element.task.text
  }
  if (element.note !== null) {
    return firstLine(element.note.text)
  }
  if (element.text !== null && element.text !== '') {
    return firstLine(element.text)
  }
  return element.label ?? ''
}

/** Whether the content this card points at is gone — which is what the visible gap is drawn from. */
export function contentIsGone(element: BoardElement): boolean {
  return (
    element.kind === 'card' &&
    element.document === null &&
    element.note === null &&
    element.task === null
  )
}

function firstLine(text: string): string {
  return (
    text
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line !== '') ?? ''
  )
}

/** The statuses that carry a code this side knows how to say out loud. */
const REFUSALS = [400, 404, 409, 413] as const

const SENTENCES: Readonly<Record<string, string>> = {
  'no-such-board': 'That board is not there any more.',
  'no-such-board-element': 'That is not on this board any more.',
  'no-such-connection': 'That line is not there any more.',
  'not-on-this-board': 'That belongs to another board.',
  'board-needs-a-name': 'A board needs a name.',
  'last-board':
    'This is your only board, and it is where signing in leads. Clear it if you want an empty surface.',
  'nothing-written-on-it': 'A sticky note has to say something.',
  'board-text-too-long':
    'That is longer than a sticky note holds. Something this long wants to be a document — write one in the library and lay a card on it.',
}

async function change(address: string, method: string, body?: unknown): Promise<unknown> {
  const response = await request(address, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    expect: [...REFUSALS],
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  if (!response.ok) {
    throw refusalIn(await response.text(), response.status)
  }

  return response.status === 204 ? null : readJson<unknown>(response)
}

function parse(body: string): { code?: string; message?: string } | null {
  try {
    return JSON.parse(body) as { code?: string; message?: string }
  } catch {
    return null
  }
}

function refusalIn(body: string, status: number): RequestFailed {
  const problem = parse(body)
  const known = problem?.code === undefined ? undefined : SENTENCES[problem.code]

  const failure: Failure = {
    kind: status >= 500 ? 'service' : 'refused',
    message: known ?? problem?.message ?? 'MyLib could not do that.',
    detail: known === undefined ? null : (problem?.message ?? null),
    // Nothing on this list gets better by being asked again: each of them is an answer.
    retryable: status >= 500,
    code: problem?.code ?? null,
  }
  return new RequestFailed(failure)
}
